/**
 * Who is signed in, which firm they are in, and what they hold.
 *
 * The session is the token in `sessionStore`; the identity is whatever
 * `GET /api/staff/me` says about it. Those are deliberately separate: the token is
 * the credential, `me` is the server's description of what that credential currently
 * means, and only the second is allowed to decide what the interface shows.
 *
 * Switching organization re-authenticates rather than filtering. The tenant comes
 * from the membership the SESSION is bound to and from nothing else (BR-02) — there
 * is no request the frontend could make to "view as another firm", and a UI that
 * looked like there was would be lying about the isolation model.
 */

import {
  PermissionSet,
  fetchMe,
  loginAndStore,
  logout as apiLogout,
  type LoginCredentials,
  type MembershipOption,
  type SessionDescription,
} from '@legalcrm/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { apiClient, sessionStore, setSessionEndedHandler } from '@/api/client';

export interface AuthState {
  /** Null until `me` has resolved. */
  me: SessionDescription | null;
  permissions: PermissionSet;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Set when login returned an organization choice instead of a session. */
  organizationChoices: MembershipOption[] | null;
  signIn: (credentials: LoginCredentials) => Promise<'signed-in' | 'choose-organization'>;
  signOut: () => Promise<void>;
  clearOrganizationChoices: () => void;
  /** What this firm calls a matter. Falls back to "Matter". */
  matterLabel: string;
  /**
   * The firm's own currency, for the few amounts that are not stored with one.
   *
   * Every money column on a record carries its own currency and that one wins -- an
   * invoice raised in euros stays in euros wherever it is read. This is the fallback for
   * an estimate a client has not been billed for yet, where there is no transaction to
   * take a currency from and the firm's own is the only honest answer.
   */
  baseCurrency: string;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [hasToken, setHasToken] = useState(() => Boolean(sessionStore.read()));
  const [organizationChoices, setOrganizationChoices] = useState<MembershipOption[] | null>(
    null,
  );

  // Another tab signing out signs this one out too.
  useEffect(() => sessionStore.subscribe((session) => setHasToken(Boolean(session))), []);

  // A refresh that could not be rescued ends the session for the whole app.
  useEffect(() => {
    setSessionEndedHandler(() => {
      setHasToken(false);
      queryClient.clear();
    });
    return () => setSessionEndedHandler(null);
  }, [queryClient]);

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => fetchMe(apiClient),
    enabled: hasToken,
    // The permission set is the thing every render reads. Re-fetching it on every
    // window focus would be wasteful; a role change is rare and takes effect on the
    // next full load or the next 401. Scope resolution, which DOES have to be live,
    // is never cached here — it is answered per record by `_actions`.
    staleTime: 5 * 60_000,
    retry: false,
  });

  const permissions = useMemo(
    () => new PermissionSet(meQuery.data?.permissions ?? []),
    [meQuery.data?.permissions],
  );

  const signIn = useCallback(
    async (credentials: LoginCredentials) => {
      const result = await loginAndStore(apiClient, credentials);
      if ('choices' in result) {
        setOrganizationChoices(result.choices);
        return 'choose-organization' as const;
      }
      setOrganizationChoices(null);
      setHasToken(true);
      // The previous tenant's data must not survive a sign-in. Clearing rather than
      // invalidating: an invalidated query briefly serves the stale value first, and
      // that stale value belongs to another firm.
      queryClient.clear();
      await queryClient.refetchQueries({ queryKey: ['me'] });
      return 'signed-in' as const;
    },
    [queryClient],
  );

  const signOut = useCallback(async () => {
    await apiLogout(apiClient);
    setHasToken(false);
    setOrganizationChoices(null);
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<AuthState>(
    () => ({
      me: meQuery.data ?? null,
      permissions,
      isAuthenticated: hasToken && Boolean(meQuery.data),
      isLoading: hasToken && meQuery.isLoading,
      organizationChoices,
      signIn,
      signOut,
      clearOrganizationChoices: () => setOrganizationChoices(null),
      matterLabel: meQuery.data?.organization?.matter_label || 'Matter',
      baseCurrency: meQuery.data?.organization?.base_currency || 'USD',
    }),
    [
      meQuery.data,
      meQuery.isLoading,
      permissions,
      hasToken,
      organizationChoices,
      signIn,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>.');
  return context;
}
